<template>
  <div class="crud_form_wrapper">
    <!-- Start:: Title -->
    <div class="form_title_wrapper">
      <h4>{{ $t("TITLES.edit_accepted_ad") }}</h4>
    </div>
    <div class="col-12 text-end">
      <v-btn @click="$router.go(-1)" style="color: #404b3e">
        <i class="fas fa-backward"></i>
      </v-btn>
    </div>
    <!-- End:: Title -->

    <!-- Start:: Single Step Form Content -->
    <div class="single_step_form_content_wrapper">
      <form @submit.prevent="validateFormInputs">
        <div class="row">
          <!-- Start:: Ad Name -->
          <base-input
            col="6"
            type="text"
            :placeholder="$t('TABLES.AcceptedAds.ad_name')"
            v-model.trim="data.name"
            required
          />
          <!-- End:: Ad Name -->

          <!-- Start:: Shop Type -->
          <base-select-input
            col="6"
            :optionsList="shopTypes"
            :placeholder="$t('PLACEHOLDERS.shop_type')"
            v-model="data.shop_type"
            :disabled="isPublished"
            required
          />
          <!-- End:: Shop Type -->

          <!-- Start:: Area -->
          <base-input
            col="6"
            type="number"
            :placeholder="$t('PLACEHOLDERS.area')"
            v-model.trim="data.area"
            required
          />
          <!-- End:: Area -->

          <!-- Start:: Advertiser Type (only for shack) -->
          <base-select-input
            col="6"
            :optionsList="advertiserTypes"
            :placeholder="$t('PLACEHOLDERS.advertiser_type')"
            v-model="data.advertiser_type"
            required
          />
          <!-- End:: Advertiser Type -->

          <!-- Start:: Ownership Deed Upload (only for shack) -->
          <base-name-preview-file-upload-input
            col="6"
            identifier="ownership_deed"
            :preSelectedFile="data.ownership_deed.path"
            :placeholder="$t('PLACEHOLDERS.ownership_deed')"
            @selectFile="selectOwnershipDeed"
          />
          <!-- End:: Ownership Deed Upload -->

          <!-- Start:: Agency Deed Upload (only for shack) -->
          <base-name-preview-file-upload-input
            v-if="
              data.advertiser_type &&
              data.advertiser_type.id === 'rental_office'
            "
            col="6"
            identifier="agency_deed"
            :preSelectedFile="data.agency_deed.path"
            :placeholder="$t('PLACEHOLDERS.agency_deed')"
            @selectFile="selectAgencyDeed"
          />
          <!-- End:: Agency Deed Upload -->

          <!-- Start:: Shop Status -->
          <base-select-input
            col="6"
            :optionsList="shopStatuses"
            :placeholder="$t('PLACEHOLDERS.shop_status')"
            v-model="data.shop_status"
            required
          />
          <!-- End:: Shop Status -->

          <!-- Start:: Payment Due -->
          <base-select-input
            col="6"
            :optionsList="paymentDueOptions"
            :placeholder="$t('PLACEHOLDERS.payment_due')"
            v-model="data.payment_due"
            required
          />
          <!-- End:: Payment Due -->

          <!-- Start:: Rent Price -->
          <base-input
            col="6"
            type="number"
            :placeholder="$t('PLACEHOLDERS.rent_price')"
            v-model.trim="data.rent_price"
            required
          />
          <!-- End:: Rent Price -->

          <!-- Start:: Allowed Activities -->
          <base-select-input
            col="6"
            :optionsList="activities"
            :placeholder="$t('PLACEHOLDERS.allowed_activities')"
            v-model="data.allowed_activities"
            multiple
          />
          <!-- End:: Allowed Activities -->

          <!-- Start:: Blocked Activities -->
          <base-select-input
            col="6"
            :optionsList="activities"
            :placeholder="$t('PLACEHOLDERS.blocked_activities')"
            v-model="data.blocked_activities"
            multiple
          />
          <!-- End:: Blocked Activities -->

          <!-- Start:: Location Section -->
          <div class="col-12 mt-4 mb-3">
            <h5
              style="
                color: #404b3e;
                border-bottom: 2px solid #404b3e;
                padding-bottom: 10px;
              "
            >
              {{ $t("TITLES.location_information") }}
            </h5>
          </div>

          <!-- Start:: Region -->
          <base-select-input
            col="4"
            :optionsList="regions"
            :placeholder="$t('PLACEHOLDERS.region')"
            v-model="data.region"
            @input="onRegionChange"
            :disabled="isPublished"
            required
          />
          <!-- End:: Region -->

          <!-- Start:: City -->
          <base-select-input
            col="4"
            :optionsList="cities"
            :placeholder="$t('PLACEHOLDERS.city')"
            v-model="data.city"
            @input="onCityChange"
            :disabled="!data.region || isPublished"
            required
          />
          <!-- End:: City -->

          <!-- Start:: District -->
          <base-select-input
            col="4"
            :optionsList="districts"
            :placeholder="$t('PLACEHOLDERS.district')"
            v-model="data.district"
            :disabled="!data.city || isPublished"
            required
          />
          <!-- End:: District -->

          {{ data.lat }}
          <!-- Start:: Location Input (Map) -->
          <div class="col-12">
            <p class="mb-2" style="color: #404b3e; font-weight: 500">
              {{ $t("PLACEHOLDERS.location_on_map") }}
            </p>
            <ChangableMapComponent
              v-if="!isPublished"
              :lat="data.lat"
              :lng="data.lng"
              @update-location="updateLocation"
            />
            <MapComponent v-else :latitude="data.lat" :longitude="data.lng" />
          </div>
          <!-- End:: Location Input (Map) -->

          <!-- Start:: Images Section -->
          <div class="col-12 mt-4 mb-3">
            <h5
              style="
                color: #404b3e;
                border-bottom: 2px solid #404b3e;
                padding-bottom: 10px;
              "
            >
              {{ $t("TITLES.ad_images") }}
            </h5>
          </div>

          <!-- Start:: Multiple Images Upload -->
          <base-multi-image-upload-input
            :urls="imgUrls.map((img) => img.image)"
            multiple
            @onFileSelect="onFileSelect"
            @onFileRemove="onFileRemove"
          >
            {{ $t("PLACEHOLDERS.ad_images") }}
          </base-multi-image-upload-input>
          <!-- End:: Multiple Images Upload -->

          <!-- Start:: Submit Button Wrapper -->
          <div class="btn_wrapper">
            <base-button
              class="mt-2"
              styleType="primary_btn"
              :btnText="$t('BUTTONS.save')"
              :isLoading="isWaitingRequest"
              :disabled="isWaitingRequest"
            />
          </div>
          <!-- End:: Submit Button Wrapper -->
        </div>
      </form>
    </div>
    <!-- END:: Single Step Form Content -->
  </div>
</template>

<script>
import { mapActions } from "vuex";
import ChangableMapComponent from "@/components/general/ChangableMapComponent.vue";
import MapComponent from "@/components/general/MapComponent.vue";

export default {
  name: "EditAcceptedAd",

  components: {
    ChangableMapComponent,
    MapComponent,
  },

  data() {
    return {
      // Start:: Loader Control Data
      isWaitingRequest: false,
      // End:: Loader Control Data

      // Start:: Data Collection To Send
      data: {
        name: null,
        shop_type: null,
        advertiser_type: null,
        ownership_deed: {
          path: null,
          file: null,
        },
        agency_deed: {
          path: null,
          file: null,
        },
        area: null,
        shop_status: null,
        payment_due: null,
        rent_price: null,
        lat: 24.7136,
        lng: 46.6753,
        region: null,
        city: null,
        district: null,
        allowed_activities: [],
        blocked_activities: [],
        ad_status: null,
      },
      // End:: Data Collection To Send

      additionalImages: [],
      imgUrls: [],

      // Start:: Options
      regions: [],
      cities: [],
      districts: [],
      activities: [],
      shopTypes: [
        {
          id: "shack",
          name: this.$t("TABLES.AcceptedAds.shack"),
        },
        {
          id: "exposition",
          name: this.$t("TABLES.AcceptedAds.exposition"),
        },
      ],
      advertiserTypes: [
        {
          id: "owner",
          name: this.$t("PLACEHOLDERS.owner"),
        },
        {
          id: "rental_office",
          name: this.$t("PLACEHOLDERS.rental_office"),
        },
      ],
      shopStatuses: [
        {
          id: "ready",
          name: this.$t("TABLES.AcceptedAds.ready"),
        },
        {
          id: "unfinished",
          name: this.$t("TABLES.AcceptedAds.unfinished"),
        },
        {
          id: "land",
          name: this.$t("TABLES.AcceptedAds.land"),
        },
      ],
      paymentDueOptions: [
        {
          id: "quarterly",
          name: this.$t("TABLES.AcceptedAds.quarterly"),
        },
        {
          id: "semi_annually",
          name: this.$t("TABLES.AcceptedAds.semi_annually"),
        },
        {
          id: "annually",
          name: this.$t("TABLES.AcceptedAds.annually"),
        },
      ],
      // End:: Options
    };
  },

  computed: {
    isPublished() {
      return this.data.status === "published";
    },
  },

  methods: {
    // Start:: Vuex Actions
    ...mapActions({
      updateAppLoadingStatus: "LoadingModule/updateAppLoadingStatus",
    }),
    // End:: Vuex Actions

    // Start:: Update Location
    updateLocation(location) {
      this.data.lat = location.lat;
      this.data.lng = location.lng;
    },
    // End:: Update Location

    // Start:: Get Regions
    async getRegions() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: "regions",
          params: {
            ignorePermissionCheck: 1,
            limit: 0,
            page: 0,
            is_active: 1,
          },
        });
        this.regions = res.data.data.data;
      } catch (error) {
        console.log(error.response?.data?.message);
      }
    },
    // End:: Get Regions

    // Start:: On Region Change
    async onRegionChange() {
      this.data.city = null;
      this.data.district = null;
      this.cities = [];
      this.districts = [];
      if (this.data.region) {
        await this.getCitiesByRegion(this.data.region.id);
      }
    },
    // End:: On Region Change

    // Start:: Get Cities By Region
    async getCitiesByRegion(regionId) {
      try {
        let res = await this.$axios({
          method: "GET",
          url: "cities",
          params: {
            regionId: regionId,
            is_active: 1,
            limit: 0,
            page: 0,
          },
        });
        this.cities = res.data.data.data;
      } catch (error) {
        console.log(error.response?.data?.message);
      }
    },
    // End:: Get Cities By Region

    // Start:: On City Change
    async onCityChange() {
      this.data.district = null;
      this.districts = [];
      if (this.data.city) {
        await this.getDistrictsByCity(this.data.city.id);
      }
    },
    // End:: On City Change

    // Start:: Get Districts By City
    async getDistrictsByCity(cityId) {
      try {
        let res = await this.$axios({
          method: "GET",
          url: "districts",
          params: {
            city_id: cityId,
            is_active: 1,
            limit: 0,
            page: 0,
          },
        });
        this.districts = res.data.data.data;
      } catch (error) {
        console.log(error.response?.data?.message);
      }
    },
    // End:: Get Districts By City

    // Start:: Get Activities
    async getActivities() {
      try {
        let res = await this.$axios({
          method: "GET",
          url: "activities",
          params: {
            is_active: 1,
            limit: 0,
            page: 0,
          },
        });
        this.activities = res.data.data.data;
      } catch (error) {
        console.log(error.response?.data?.message);
      }
    },
    // End:: Get Activities

    // Start:: On File Select
    onFileSelect(files) {
      this.additionalImages = files;
    },
    // End:: On File Select

    // Start:: Select Ownership Deed
    selectOwnershipDeed(selectedFile) {
      this.data.ownership_deed = selectedFile;
    },
    // End:: Select Ownership Deed

    // Start:: Select Agency Deed
    selectAgencyDeed(selectedFile) {
      this.data.agency_deed = selectedFile;
    },
    // End:: Select Agency Deed

    // Start:: On File Remove
    async onFileRemove(index) {
      const removedImage = this.imgUrls[index];
      if (removedImage?.id) {
        try {
          await this.$axios({
            method: "DELETE",
            url: `ads/${this.$route.params.id}/images/${removedImage.id}`,
          });
          this.imgUrls.splice(index, 1);
          this.$message.success(this.$t("MESSAGES.deletedSuccessfully"));
        } catch (error) {
          this.$message.error(error.response?.data?.message);
        }
      }
    },
    // End:: On File Remove

    // Start:: Validate Form Inputs
    validateFormInputs() {
      this.isWaitingRequest = true;

      if (!this.data.name) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.ad_name"));
        return;
      } else if (!this.data.shop_type) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.shop_type"));
        return;
      } else if (!this.data.area) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.area"));
        return;
      } else if (!this.data.shop_status) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.shop_status"));
        return;
      } else if (!this.data.payment_due) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.payment_due"));
        return;
      } else if (!this.data.rent_price) {
        this.isWaitingRequest = false;
        this.$message.error(this.$t("VALIDATION.rent_price"));
        return;
      } else {
        this.submitForm();
        return;
      }
    },
    // End:: Validate Form Inputs

    // Start:: Submit Form
    async submitForm() {
      const REQUEST_DATA = new FormData();

      // Start:: Append Request Data
      REQUEST_DATA.append("name", this.data.name);

      // Only append shop_type if not published
      if (!this.isPublished) {
        REQUEST_DATA.append(
          "shop_type",
          this.data.shop_type.id || this.data.shop_type
        );
      }

      // Append advertiser_type if shop_type is shack
      if (this.data.advertiser_type) {
        REQUEST_DATA.append(
          "advertiser_type",
          this.data.advertiser_type.id || this.data.advertiser_type
        );
      }

      // Append ownership_deed file if exists
      if (this.data.ownership_deed.file) {
        REQUEST_DATA.append("ownership_deed", this.data.ownership_deed.file);
      }

      if (
        this.data.advertiser_type &&
        this.data.advertiser_type.id === "rental_office"
      ) {
        // Append agency_deed file if exists
        if (this.data.agency_deed.file) {
          REQUEST_DATA.append("agency_deed", this.data.agency_deed.file);
        }
      }

      REQUEST_DATA.append("area", this.data.area);
      REQUEST_DATA.append(
        "shop_status",
        this.data.shop_status.id || this.data.shop_status
      );
      REQUEST_DATA.append(
        "payment_due",
        this.data.payment_due.id || this.data.payment_due
      );
      REQUEST_DATA.append("rent_price", this.data.rent_price);

      // Only append location fields if not published
      if (this.isPublished) {
        REQUEST_DATA.append("lat", this.data.lat);
        REQUEST_DATA.append("lng", this.data.lng);

        // Append region, city, district
        if (this.data.region) {
          REQUEST_DATA.append("region_id", this.data.region.id);
        }
        if (this.data.city) {
          REQUEST_DATA.append("city_id", this.data.city.id);
        }
        if (this.data.district) {
          REQUEST_DATA.append("district_id", this.data.district.id);
        }
      }

      // Append allowed activities
      if (
        this.data.allowed_activities &&
        this.data.allowed_activities.length > 0
      ) {
        this.data.allowed_activities.forEach((activity, index) => {
          REQUEST_DATA.append(`allowed_activities[${index}]`, activity.id);
        });
      }

      // Append blocked activities
      if (
        this.data.blocked_activities &&
        this.data.blocked_activities.length > 0
      ) {
        this.data.blocked_activities.forEach((activity, index) => {
          REQUEST_DATA.append(`blocked_activities[${index}]`, activity.id);
        });
      }

      // Append new images
      if (this.additionalImages.length) {
        this.additionalImages.forEach((file) => {
          REQUEST_DATA.append("images[]", file);
        });
      }

      REQUEST_DATA.append("_method", "PUT");
      // End:: Append Request Data

      try {
        await this.$axios({
          method: "POST",
          url: `ads/${this.$route.params.id}`,
          data: REQUEST_DATA,
        });
        this.isWaitingRequest = false;
        this.$message.success(this.$t("MESSAGES.editedSuccessfully"));
        this.$router.push({ path: "/accepted-ads/all" });
      } catch (error) {
        this.isWaitingRequest = false;
        // console.log("error.response?.data", error.response);
        this.$message.error(error.response?.data?.message);
      }
    },
    // End:: Submit Form

    // Start:: Get Ad Data
    async getAdData() {
      this.updateAppLoadingStatus(true);

      try {
        let res = await this.$axios({
          method: "GET",
          url: `ads/${this.$route.params.id}`,
        });

        const adData = res.data.data.ad;

        this.data.status = adData.status;
        this.data.ad_status = adData.ad_status;
        this.data.name = adData.name;
        this.data.shop_type = {
          id: adData.shop_type,
          name:
            adData.shop_type === "shack"
              ? this.$t("TABLES.AcceptedAds.shack")
              : this.$t("TABLES.AcceptedAds.exposition"),
        };

        // Load advertiser_type if shop_type is shack
        if (adData.advertiser_type) {
          this.data.advertiser_type = {
            id: adData.advertiser_type,
            name:
              adData.advertiser_type === "owner"
                ? this.$t("PLACEHOLDERS.owner")
                : this.$t("PLACEHOLDERS.rental_office"),
          };
        }

        // Load ownership_deed path if exists
        if (adData.ownership_deed) {
          this.data.ownership_deed.path = adData.ownership_deed;
        }

        // Load agency_deed path if exists
        if (adData.agency_deed) {
          this.data.agency_deed.path = adData.agency_deed;
        }

        this.data.area = adData.area;
        this.data.shop_status = {
          id: adData.shop_status,
          name:
            adData.shop_status === "ready"
              ? this.$t("TABLES.AcceptedAds.ready")
              : adData.shop_status === "unfinished"
              ? this.$t("TABLES.AcceptedAds.unfinished")
              : this.$t("TABLES.AcceptedAds.land"),
        };
        this.data.payment_due = {
          id: adData.payment_due,
          name:
            adData.payment_due === "quarterly"
              ? this.$t("TABLES.AcceptedAds.quarterly")
              : adData.payment_due === "semi_annually"
              ? this.$t("TABLES.AcceptedAds.semi_annually")
              : this.$t("TABLES.AcceptedAds.annually"),
        };
        this.data.rent_price = adData.rent_price;
        this.data.lat = parseFloat(adData.lat) || 24.7136;
        this.data.lng = parseFloat(adData.lng) || 46.6753;

        // Set region, city, district
        this.data.region = adData.region;
        this.data.city = adData.city;
        this.data.district = adData.district;

        // Load cities and districts if region/city exists
        if (this.data.region) {
          await this.getCitiesByRegion(this.data.region.id);
        }
        if (this.data.city) {
          await this.getDistrictsByCity(this.data.city.id);
        }

        // Process allowed activities
        if (adData.allowed_activities && adData.allowed_activities.length) {
          this.data.allowed_activities = adData.allowed_activities;
        }

        // Process blocked activities
        if (adData.blocked_activities && adData.blocked_activities.length) {
          this.data.blocked_activities = adData.blocked_activities;
        }

        // Process existing images
        if (adData.images && adData.images.length) {
          this.imgUrls = adData.images.map((img, index) => ({
            id: img.id,
            image: img.image,
            index,
          }));
        }

        this.updateAppLoadingStatus(false);
      } catch (error) {
        this.updateAppLoadingStatus(false);
        this.$message.error(error.response?.data?.message);
      }
    },
    // End:: Get Ad Data
  },

  created() {
    this.getRegions();
    this.getActivities();
    this.getAdData();
  },
};
</script>
